import SiteNav from '../../components/SiteNav';
import Footer from '../home/components/Footer';
import type { AreaPageContent } from '../../data/areas';
import { AREA_PAGES } from '../../data/areas';
import { SERVICES } from '../../data/services';
import { BUSINESS } from '../../site';

interface AreaPageProps {
  area: AreaPageContent;
}

export default function AreaPage({ area }: AreaPageProps) {
  const otherAreas = AREA_PAGES.filter((a) => a.path !== area.path);

  return (
    <div className="min-h-screen bg-white">
      <SiteNav />

      <main>
        {/* Header */}
        <section
          className="px-6 pb-20 text-white"
          style={{ background: 'linear-gradient(135deg,#0b5b96,#083f68)', paddingTop: 'calc(clamp(84px, 9vw, 116px) + 48px)' }}
        >
          <div className="max-w-[860px] mx-auto">
            <nav aria-label="Breadcrumb" className="mb-6 text-[14px] text-white/75">
              <ol className="flex flex-wrap items-center gap-2">
                <li><a href="/" className="hover:text-white">Home</a></li>
                <li aria-hidden="true">›</li>
                <li aria-current="page" className="text-white">{area.name}</li>
              </ol>
            </nav>
            <h1 className="font-extrabold leading-[1.1] tracking-tight mb-6" style={{ fontSize: 'clamp(2.25rem,4.5vw,3.5rem)' }}>
              {area.h1}
            </h1>
            <p className="text-[19px] text-white/[.92] leading-relaxed mb-10">{area.intro}</p>
            <div className="flex flex-wrap items-center gap-4">
              <a
                href={BUSINESS.phoneHref}
                className="flex items-center gap-[10px] text-[#1c2410] px-[30px] py-[15px] rounded-full font-bold text-[17px] bg-[#5FA42B] hover:bg-[#4C8A24] transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap"
              >
                <i className="ri-phone-fill"></i> Call {BUSINESS.phoneDisplay}
              </a>
              <a
                href={`mailto:${BUSINESS.email}`}
                className="flex items-center gap-[10px] bg-white text-[#0b5b96] px-[30px] py-[15px] rounded-full font-bold text-[17px] transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap"
              >
                <i className="ri-mail-fill"></i> Email for a Free Quote
              </a>
            </div>
          </div>
        </section>

        {/* Local highlights */}
        <section className="py-24 px-6 bg-gradient-to-b from-white to-[#f7f8fa]">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-extrabold text-[#161a20] leading-[1.15] tracking-tight mb-12 text-center" style={{ fontSize: 'clamp(2rem,3.5vw,2.6rem)' }}>
              {area.highlightsHeading}
            </h2>
            <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
              {area.highlights.map((item) => (
                <div key={item.title} className="bg-white rounded-[20px] p-[34px_30px] border border-[#edeef0] shadow-[0_1px_2px_rgba(16,24,40,0.03)]">
                  <div className="w-14 h-14 rounded-2xl bg-[#0b5b96]/[.09] flex items-center justify-center mb-[22px]">
                    <i className="ri-building-2-line text-[26px] text-[#0b5b96]"></i>
                  </div>
                  <h3 className="text-[19px] font-bold text-[#161a20] mb-[10px]">{item.title}</h3>
                  <p className="text-[15.5px] leading-relaxed text-[#5b6472]">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Local detail */}
        <section className="py-24 px-6 bg-white">
          <div className="max-w-[860px] mx-auto flex flex-col gap-12">
            {area.sections.map((section) => (
              <div key={section.heading}>
                <h2 className="font-extrabold text-[#161a20] leading-[1.2] tracking-tight mb-4" style={{ fontSize: 'clamp(1.6rem,2.8vw,2rem)' }}>
                  {section.heading}
                </h2>
                <p className="text-[17px] leading-relaxed text-[#5b6472]">{section.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Services */}
        <section className="py-24 px-6 bg-[#f7f8fa]">
          <div className="max-w-7xl mx-auto">
            <p className="text-[13px] font-bold tracking-[0.09em] uppercase text-[#0b5b96] mb-[14px]">Our Services</p>
            <h2 className="font-extrabold text-[#161a20] leading-[1.15] tracking-tight mb-12" style={{ fontSize: 'clamp(2rem,3.5vw,2.6rem)' }}>
              Cleaning services in {area.name}
            </h2>
            <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              {SERVICES.map((service) => (
                <div key={service.title} className="bg-white rounded-[20px] p-8">
                  <div className="w-12 h-12 rounded-[14px] bg-[#5FA42B]/[.18] flex items-center justify-center mb-[18px]">
                    <i className={`${service.icon} text-[22px] text-[#0b5b96]`}></i>
                  </div>
                  <h3 className="text-[19px] font-bold text-[#161a20] mb-3">{service.title}</h3>
                  <p className="text-[15.5px] leading-relaxed text-[#5b6472]">{service.description}</p>
                </div>
              ))}
            </div>
            <a href="/#services" className="inline-flex items-center gap-2 mt-10 font-bold text-[16px] text-[#0b5b96] hover:text-[#084876]">
              See all of our services
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </section>

        {/* Contact CTA */}
        <section id="contact" className="py-24 px-6 bg-white">
          <div
            className="max-w-[860px] mx-auto rounded-[28px] shadow-[0_24px_48px_-24px_rgba(11,91,150,0.4)] p-12 text-white"
            style={{ background: 'linear-gradient(135deg,#0b5b96,#083f68)' }}
          >
            <h2 className="font-extrabold leading-[1.15] tracking-tight mb-4" style={{ fontSize: 'clamp(1.8rem,3.2vw,2.4rem)' }}>
              Get a free cleaning quote in {area.name}
            </h2>
            <p className="text-[17px] text-white/[.9] leading-relaxed mb-8">
              Use our online form, or call, text or email and tell us about your space. We’ll respond within 24 hours with a free, no-obligation quote.
            </p>
            <a
              href="/#contact"
              className="inline-flex items-center gap-[10px] text-[#1c2410] px-[30px] py-[15px] rounded-full font-bold text-[17px] bg-[#5FA42B] hover:bg-[#4C8A24] transition-all duration-200 hover:-translate-y-0.5 mb-10"
            >
              Request a Quote Online
              <i className="ri-arrow-right-line"></i>
            </a>
            <div className="grid gap-6 sm:grid-cols-3">
              <div>
                <p className="text-[13.5px] text-white/75 mb-1">Call or Text</p>
                <a href={BUSINESS.phoneHref} className="text-[20px] font-bold text-white hover:text-[#5FA42B]">{BUSINESS.phoneDisplay}</a>
              </div>
              <div>
                <p className="text-[13.5px] text-white/75 mb-1">Email Us</p>
                <a href={`mailto:${BUSINESS.email}`} className="text-[15.5px] text-white hover:text-[#5FA42B] break-all">{BUSINESS.email}</a>
              </div>
              <div>
                <p className="text-[13.5px] text-white/75 mb-1">Business Hours</p>
                <p className="text-[15.5px] leading-relaxed">{BUSINESS.hours.label}<br />{BUSINESS.hours.time}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Other areas */}
        <section className="pb-24 px-6 bg-white">
          <div className="max-w-[860px] mx-auto">
            <h2 className="text-[22px] font-bold text-[#161a20] mb-5">Other areas we serve</h2>
            <ul className="flex flex-wrap gap-3">
              {otherAreas.map((other) => (
                <li key={other.path}>
                  <a
                    href={other.path}
                    className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full text-[15px] font-medium hover:border-[#5FA42B] hover:text-[#0b5b96]"
                  >
                    <i className="ri-map-pin-2-line"></i>
                    {other.h1.replace(/, VA$/, '')}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="/"
                  className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full text-[15px] font-medium hover:border-[#5FA42B] hover:text-[#0b5b96]"
                >
                  <i className="ri-home-4-line"></i>
                  Flip Ready homepage
                </a>
              </li>
            </ul>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
